import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatPLN } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { Search, Plus, ShoppingCart, Filter, Sparkle, ChevronDown, ChevronUp } from "lucide-react";

const QUOTED_COLLAPSED_KEY = "ol_quoted_collapsed_v1";

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [quoted, setQuoted] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("Wszystkie");
  const [quotedCollapsed, setQuotedCollapsed] = useState(() => localStorage.getItem(QUOTED_COLLAPSED_KEY) === "1");
  const { addItem, items, count, total } = useCart();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/custom-product-requests/quoted").then((r) => setQuoted(r.data || []));
  }, []);

  const toggleQuoted = () => {
    setQuotedCollapsed((c) => {
      const next = !c;
      localStorage.setItem(QUOTED_COLLAPSED_KEY, next ? "1" : "0");
      return next;
    });
  };

  const addQuoted = (q) => {
    addItem(
      {
        id: `custom:${q.id}`,
        custom_request_id: q.id,
        name: q.product_name,
        sku: `CUSTOM-${q.id.slice(0, 8).toUpperCase()}`,
        unit: "szt.",
        price_net: q.estimated_price_net,
        category: "Wycena Office-Land",
      },
      1,
    );
    toast.success(`Dodano wycenę: ${q.product_name}`);
  };

  const categories = useMemo(() => {
    const set = new Set(products.map((p) => p.category));
    return ["Wszystkie", ...Array.from(set).sort()];
  }, [products]);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (category !== "Wszystkie" && p.category !== category) return false;
      if (search && !`${p.name} ${p.sku}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [products, search, category]);

  return (
    <div>
      <PageHeader
        testId="catalog-page"
        title="Katalog produktów"
        subtitle={`${filtered.length} z ${products.length} produktów`}
        actions={
          <Link
            to="/cart"
            data-testid="catalog-cart-link"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-sm font-semibold transition-colors"
          >
            <ShoppingCart className="w-4 h-4" /> Koszyk · {count} poz. · {formatPLN(total)}
          </Link>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        <aside className="ol-card p-4 h-fit">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5"><Filter className="w-3 h-3" /> Kategorie</p>
          <div className="space-y-0.5">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`cat-${c}`}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  category === c ? "bg-[#FFF4ED] text-[#CC4B00] font-semibold" : "text-slate-700 hover:bg-slate-50"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </aside>

        <div>
          {quoted.length > 0 && (
            <div className="ol-card mb-4 border-2 border-[#FF6B1A]/30 bg-gradient-to-br from-[#FFF4ED] to-white overflow-hidden" data-testid="quoted-section">
              <button
                type="button"
                onClick={toggleQuoted}
                data-testid="quoted-toggle"
                className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-[#FFF4ED]/60 transition-colors"
                aria-expanded={!quotedCollapsed}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Sparkle className="w-4 h-4 text-[#CC4B00] flex-shrink-0" />
                  <span className="text-xs font-bold uppercase tracking-widest text-[#CC4B00]">
                    Wyceny Office-Land
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FF6B1A] text-white font-bold flex-shrink-0">
                    {quoted.length}
                  </span>
                </div>
                {quotedCollapsed
                  ? <ChevronDown className="w-4 h-4 text-[#CC4B00] flex-shrink-0" />
                  : <ChevronUp className="w-4 h-4 text-[#CC4B00] flex-shrink-0" />}
              </button>
              {!quotedCollapsed && (
                <div className="px-4 pb-4">
                  <p className="text-xs text-slate-600 mb-3">
                    Office-Land przygotowało wycenę dla {quoted.length === 1 ? "Twojego zapytania" : `Twoich ${quoted.length} zapytań`} —
                    możesz dodać je do zamówienia jednorazowo z cenami poniżej.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {quoted.map((q) => (
                      <div key={q.id} className="flex items-center gap-3 p-3 rounded-md border border-slate-200 bg-white" data-testid={`quoted-${q.id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-900 truncate">{q.product_name}</p>
                          <p className="text-xs text-slate-500">
                            Cena netto Office-Land: <strong className="text-[#CC4B00]">{formatPLN(q.estimated_price_net)}</strong>
                          </p>
                          {q.office_land_comment && <p className="text-[11px] text-slate-500 mt-0.5">{q.office_land_comment}</p>}
                        </div>
                        <button
                          onClick={() => addQuoted(q)}
                          data-testid={`add-quoted-${q.id}`}
                          className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-xs font-semibold flex-shrink-0"
                        >
                          <Plus className="w-3.5 h-3.5" /> Dodaj
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="relative mb-4">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="catalog-search"
              placeholder="Szukaj produktu lub SKU…"
              className="w-full pl-9 pr-3 py-2.5 rounded-md border border-slate-300 focus:border-[#FF6B1A] focus:ring-2 focus:ring-[#FF6B1A]/30 focus:outline-none text-sm"
            />
          </div>

          <div className="ol-card overflow-hidden hidden md:block">
            <table className="ol-table" data-testid="catalog-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Nazwa</th>
                  <th>Kategoria</th>
                  <th>Jedn.</th>
                  <th className="text-right">Cena netto</th>
                  <th className="text-right">Akcja</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const inCart = items[p.id]?.quantity || 0;
                  return (
                    <tr key={p.id}>
                      <td className="font-mono text-xs text-slate-500 align-top pt-3">{p.sku}</td>
                      <td>
                        <p className="font-medium text-slate-900">{p.name}</p>
                        {p.description && <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{p.description}</p>}
                      </td>
                      <td className="align-top pt-3"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{p.category}</span></td>
                      <td className="text-slate-600 align-top pt-3">{p.unit}</td>
                      <td className="text-right font-semibold align-top pt-3">{formatPLN(p.price_net)}</td>
                      <td className="text-right align-top pt-2">
                        <button
                          onClick={() => { addItem(p, 1); toast.success(`Dodano: ${p.name}`); }}
                          data-testid={`add-product-${p.sku}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-300 hover:border-[#FF6B1A] hover:text-[#FF6B1A] text-xs font-semibold transition-colors"
                        >
                          <Plus className="w-3.5 h-3.5" /> Dodaj{inCart > 0 && ` (${inCart})`}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-10 text-sm text-slate-500">Brak wyników</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="md:hidden space-y-3" data-testid="catalog-mobile-list">
            {filtered.map((p) => {
              const inCart = items[p.id]?.quantity || 0;
              return (
                <div key={p.id} className="ol-card p-4" data-testid={`catalog-card-${p.sku}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-mono text-[10px] text-slate-400 uppercase">{p.sku}</p>
                      <p className="font-semibold text-slate-900 text-sm leading-tight">{p.name}</p>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 flex-shrink-0">{p.category}</span>
                  </div>
                  {p.description && (
                    <p className="text-xs text-slate-500 leading-relaxed mb-3">{p.description}</p>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-slate-400">Cena netto · {p.unit}</p>
                      <p className="text-base font-bold text-slate-900">{formatPLN(p.price_net)}</p>
                    </div>
                    <button
                      onClick={() => { addItem(p, 1); toast.success(`Dodano: ${p.name}`); }}
                      data-testid={`add-product-mobile-${p.sku}`}
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-sm font-semibold transition-colors"
                    >
                      <Plus className="w-4 h-4" /> Dodaj{inCart > 0 && ` (${inCart})`}
                    </button>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="ol-card p-8 text-center text-sm text-slate-500">Brak wyników</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
